import { child, get, set } from "firebase/database";
import { Timestamp, deleteDoc, doc, getDocs, orderBy, query, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../../core/utils/firebase";
import { rowDataObject } from "./DataTableInterfaces";
import { IDataTableRepository } from "./DataTableRepository";

export class FirebaseDataTableRepository extends IDataTableRepository {
    uploadFile: (filename: string, blob: Blob) => Promise<string> = async (filename: string, blob: Blob) => {
        const storageRef = ref(storage, `${this.name}/${filename}`);
        await uploadBytes(storageRef, blob);
        return await getDownloadURL(storageRef);
    }
    getAll: () => Promise<Object[]> = async () => {
        const getAllQuery = query(
            this.dataCollection,
            orderBy('createdAt', "desc"),
        );
        const data = await getDocs(getAllQuery);
        return data.docs.map<Object>(d => d.data() as Object);
    }
    add: (data: Object) => Promise<Object> = async (data: Object) => {
        const docRef = doc(this.dataCollection);
        let newData = rowDataObject<Object>(data);
        newData.id = docRef.id;
        this.columns.filter(col => [
            'date',
            'datetime',
            'image',
            'file',
            'hashtags',
        ].includes(col.type)).forEach(async col => {
            if ((col.type === 'date' || col.type === 'datetime') && newData[col.field]) {
                newData[col.field] = Timestamp.fromDate(new Date(newData[col.field]));
            } else if (col.type === 'hashtags') {
                newData[col.field] = [...new Set((newData[col.field] as string).split(",").map(hashtag => hashtag.trim()))];
                const thisDataHashTagsRef = child(this.realDbRef, col.field);
                let thisDataHashTags: Set<string>;
                try {
                    const thisDataHashTagsSnap = await get(thisDataHashTagsRef);
                    thisDataHashTags = thisDataHashTagsSnap.exists() ? new Set([...thisDataHashTagsSnap.val()]) : new Set();
                } catch (error) {
                    thisDataHashTags = new Set();
                }
                for (const hashtag of newData[col.field]) {
                    thisDataHashTags.add(hashtag);
                }
                set(thisDataHashTagsRef, [...thisDataHashTags]);
            }
        })
        newData.createdAt = Timestamp.now();
        newData.updatedAt = Timestamp.now();
        await setDoc(docRef, newData);
        return newData;
    }
    update: (data: Object, id: string) => Promise<Object> = async (data: Object, id: string) => {
        try {
            let newData = rowDataObject<Object>(data);
            this.columns.filter(col => [
                'date',
                'datetime',
                'image',
                'file',
                'hashtags',
            ].includes(col.type)).forEach(async col => {
                if ((col.type === 'date' || col.type === 'datetime') && newData[col.field]) {
                    newData[col.field] = Timestamp.fromDate(new Date(newData[col.field]));
                } else if (col.type === 'hashtags') {
                    newData[col.field] = [...new Set((newData[col.field] as string).split(",").map(hashtag => hashtag.trim()))];
                    const thisDataHashTagsRef = child(this.realDbRef, col.field);
                    let thisDataHashTags: Set<string>;
                    try {
                        const thisDataHashTagsSnap = await get(thisDataHashTagsRef);
                        thisDataHashTags = thisDataHashTagsSnap.exists() ? new Set([...thisDataHashTagsSnap.val()]) : new Set();
                    } catch (error) {
                        thisDataHashTags = new Set();
                    }
                    for (const hashtag of newData[col.field]) {
                        thisDataHashTags.add(hashtag);
                    }
                    set(thisDataHashTagsRef, [...thisDataHashTags]);
                }
            })
            newData.updatedAt = Timestamp.now();
            delete newData.createdAt;
            await updateDoc(doc(firestore, `${this.name}/${id}`), newData);
            return newData;
        } catch (error) {
            console.error("ERROR UPDATING THE DATA:", error)
        }
    }
    delete: (id: string) => Promise<void> = async (id: string) => {
        await deleteDoc(doc(firestore, `${this.name}/${id}`));
    }
}